import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Headphones, Plus, Send, Clock, CheckCircle, AlertCircle, MessageSquare, Loader2, ChevronLeft, User, ArrowLeft } from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';

const STATUS_MAP = {
  open: { label: 'Ouvert', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-700' },
  resolved: { label: 'Résolu', color: 'bg-emerald-100 text-emerald-700' },
  closed: { label: 'Fermé', color: 'bg-gray-100 text-gray-500' },
};
const PRIORITY_MAP = { high: 'Haute', medium: 'Moyenne', low: 'Basse', critical: 'Critique' };
const CATEGORY_OPTIONS = [
  { value: 'general', label: 'Général' },
  { value: 'account', label: 'Mon compte' },
  { value: 'technical', label: 'Problème technique' },
  { value: 'content', label: 'Contenu' },
  { value: 'bug', label: 'Bug' },
  { value: 'other', label: 'Autre' },
];

const MesSupportPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTicket, setNewTicket] = useState({ title: '', description: '', category: 'general', priority: 'medium' });
  const messagesEndRef = useRef(null);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchTickets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tickets`, { headers });
      setTickets(res.data);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [selected?.messages?.length, selected?.id]);

  const createTicket = async () => {
    if (!newTicket.title.trim() || !newTicket.description.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/tickets`, newTicket, { headers });
      toast.success('Ticket créé avec succès');
      setShowNew(false);
      setNewTicket({ title: '', description: '', category: 'general', priority: 'medium' });
      await fetchTickets();
    } catch { toast.error('Erreur lors de la création'); }
    setCreating(false);
  };

  const sendMessage = async () => {
    if (!msgText.trim() || !selected) return;
    setSending(true);
    try {
      await axios.post(`${API}/tickets/${selected.id}/messages`, { content: msgText }, { headers });
      setMsgText('');
      const res = await axios.get(`${API}/tickets`, { headers });
      setTickets(res.data);
      const refreshed = res.data.find(t => t.id === selected.id);
      if (refreshed) setSelected(refreshed);
    } catch { toast.error('Erreur envoi'); }
    setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const formatDate = (d) => {
    try { return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return d; }
  };
  const formatTime = (d) => {
    try { return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };
  const formatDateShort = (d) => {
    try { return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }); }
    catch { return ''; }
  };

  const isAdmin = (role) => ['super_admin', 'maintenance'].includes(role);

  // ---- DETAIL VIEW ----
  if (selected) {
    const st = STATUS_MAP[selected.status] || STATUS_MAP.open;
    return (
      <div className="min-h-full" style={{ fontFamily: "'Nunito', sans-serif" }} data-testid="support-detail-view">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <button onClick={() => setSelected(null)} className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#35AFE4] transition-colors mb-5" data-testid="back-to-list">
            <ArrowLeft size={16} /> Retour à mes tickets
          </button>

          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-lg font-bold text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>{selected.title}</h2>
                <span className={`text-[11px] font-bold px-3 py-1 rounded-full flex-shrink-0 ${st.color}`}>{st.label}</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">Créé le {formatDate(selected.created_at)} — {CATEGORY_OPTIONS.find(c => c.value === selected.category)?.label || selected.category}</p>
              {selected.assignee_name && (
                <p className="text-xs text-[#35AFE4] mt-1 flex items-center gap-1">
                  <Headphones size={11} /> Pris en charge par {selected.assignee_name}
                </p>
              )}
            </div>

            {/* Messages */}
            <div className="p-5 space-y-4 bg-[#fafbfc] max-h-[450px] overflow-y-auto" data-testid="messages-thread">
              {/* Initial description */}
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-[#35AFE4] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-[10px] font-bold">{currentUser.first_name?.[0]}{currentUser.last_name?.[0]}</span>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-gray-800">Vous</span>
                    <span className="text-[10px] text-gray-400">{formatDateShort(selected.created_at)} à {formatTime(selected.created_at)}</span>
                  </div>
                  <div className="p-3.5 bg-white border border-gray-100 rounded-xl rounded-tl-sm text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                    {selected.description}
                  </div>
                </div>
              </div>

              {/* Thread messages */}
              {(selected.messages || []).map((msg) => {
                const adminMsg = isAdmin(msg.sender_role);
                return (
                  <div key={msg.id} className={`flex gap-3 ${adminMsg ? '' : 'flex-row'}`}>
                    {adminMsg ? (
                      <>
                        <div className="w-8 h-8 rounded-full bg-[#1e3a5f] flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Headphones size={13} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-bold text-[#35AFE4]">{msg.sender_name || 'Support'}</span>
                            <span className="text-[10px] text-gray-400">{formatDateShort(msg.created_at)} à {formatTime(msg.created_at)}</span>
                          </div>
                          <div className="p-3.5 bg-[#1e3a5f] text-white rounded-xl rounded-tl-sm text-sm leading-relaxed whitespace-pre-wrap">
                            {msg.content}
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="w-8 h-8 rounded-full bg-[#35AFE4] flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-white text-[10px] font-bold">{currentUser.first_name?.[0]}{currentUser.last_name?.[0]}</span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-bold text-gray-800">Vous</span>
                            <span className="text-[10px] text-gray-400">{formatDateShort(msg.created_at)} à {formatTime(msg.created_at)}</span>
                          </div>
                          <div className="p-3.5 bg-white border border-gray-100 rounded-xl rounded-tl-sm text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                            {msg.content}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply input */}
            {selected.status !== 'closed' && (
              <div className="p-4 border-t border-gray-100 bg-white">
                <div className="flex gap-2">
                  <textarea value={msgText} onChange={e => setMsgText(e.target.value)} onKeyDown={handleKeyDown}
                    rows={2} placeholder="Votre message... (Entrée pour envoyer)"
                    className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-[#35AFE4] focus:border-transparent outline-none resize-none"
                    data-testid="reply-input" />
                  <button onClick={sendMessage} disabled={sending || !msgText.trim()}
                    className="px-4 py-2.5 bg-[#1e3a5f] hover:bg-[#2a5a8f] text-white rounded-xl transition-colors flex items-center justify-center disabled:opacity-40 self-end"
                    data-testid="send-reply-btn">
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  </button>
                </div>
              </div>
            )}
            {selected.status === 'closed' && (
              <div className="p-4 border-t border-gray-100 bg-gray-50 text-center">
                <p className="text-xs text-gray-400">Ce ticket est fermé.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---- LIST VIEW ----
  return (
    <div className="min-h-full" style={{ fontFamily: "'Nunito', sans-serif" }} data-testid="support-list-view">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center shadow-md">
              <Headphones size={18} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>Support Technique</h1>
              <p className="text-xs text-gray-400">Besoin d'aide ? Créez un ticket et suivez sa résolution</p>
            </div>
          </div>
          <button onClick={() => setShowNew(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#1e3a5f] hover:bg-[#2a5a8f] text-white text-sm font-bold rounded-xl transition-colors shadow-sm"
            data-testid="new-ticket-btn">
            <Plus size={16} /> Nouveau ticket
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-[#35AFE4]" /></div>
        ) : tickets.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#eaf6fc] flex items-center justify-center mx-auto mb-4">
              <Headphones size={28} className="text-[#35AFE4]" />
            </div>
            <h3 className="text-base font-bold text-gray-800 mb-1" style={{ fontFamily: "'Barlow', sans-serif" }}>Aucun ticket</h3>
            <p className="text-sm text-gray-400 mb-5">Vous n'avez pas encore soumis de demande de support.</p>
            <button onClick={() => setShowNew(true)}
              className="px-5 py-2.5 bg-[#1e3a5f] text-white text-sm font-bold rounded-xl hover:bg-[#2a5a8f] transition-colors"
              data-testid="new-ticket-empty-btn">
              Créer mon premier ticket
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="tickets-list">
            {tickets.map((t) => {
              const st = STATUS_MAP[t.status] || STATUS_MAP.open;
              const msgCount = t.messages?.length || 0;
              const lastMsg = t.messages?.[t.messages.length - 1];
              const hasAdminReply = t.messages?.some(m => isAdmin(m.sender_role));
              return (
                <button key={t.id} onClick={() => setSelected(t)}
                  className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm p-5 text-left hover:border-[#35AFE4]/30 hover:shadow-md transition-all group"
                  data-testid={`ticket-${t.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-gray-900 group-hover:text-[#1e3a5f] transition-colors">{t.title}</h3>
                      <p className="text-xs text-gray-400 mt-1">{formatDate(t.created_at)}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {msgCount > 0 && (
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 ${hasAdminReply ? 'bg-[#eaf6fc] text-[#35AFE4]' : 'bg-gray-100 text-gray-500'}`}>
                          <MessageSquare size={9} /> {msgCount}
                        </span>
                      )}
                      <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${st.color}`}>{st.label}</span>
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 line-clamp-2">{t.description}</p>
                  {lastMsg && isAdmin(lastMsg.sender_role) && (
                    <div className="mt-3 p-2.5 bg-[#f4fafe] rounded-lg border border-[#d0e8f0]/50">
                      <p className="text-[10px] font-bold text-[#35AFE4] mb-0.5 flex items-center gap-1"><Headphones size={9} /> Dernière réponse du support</p>
                      <p className="text-xs text-gray-600 line-clamp-1">{lastMsg.content}</p>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* New ticket dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1e3a5f]" style={{ fontFamily: "'Barlow', sans-serif" }}>Nouveau ticket de support</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Sujet</label>
              <input value={newTicket.title} onChange={e => setNewTicket(p => ({ ...p, title: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-[#35AFE4] focus:border-transparent outline-none"
                placeholder="Décrivez brièvement votre problème" data-testid="new-ticket-title" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Catégorie</label>
              <select value={newTicket.category} onChange={e => setNewTicket(p => ({ ...p, category: e.target.value }))}
                className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm" data-testid="new-ticket-category">
                {CATEGORY_OPTIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold text-gray-400 uppercase block mb-1">Description</label>
              <textarea value={newTicket.description} onChange={e => setNewTicket(p => ({ ...p, description: e.target.value }))}
                rows={5} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:ring-2 focus:ring-[#35AFE4] focus:border-transparent outline-none resize-none"
                placeholder="Expliquez votre problème en détail..." data-testid="new-ticket-description" />
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setShowNew(false)}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                Annuler
              </button>
              <button onClick={createTicket} disabled={creating}
                className="flex-1 px-4 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-sm font-bold hover:bg-[#2a5a8f] disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="submit-ticket-btn">
                {creating ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Envoyer
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MesSupportPage;
